import { baseApi as api } from './baseApi';

/**
 * Los anuncios de una lámina — el modal a pantalla completa que frena al agente al entrar.
 *
 * Antes eran una lista en el código (`features/avisos/catalogo.ts`): cada anuncio pedía un
 * deploy y no había forma de saber si la gente lo leía o lo sacaba de encima. Ahora se
 * crean, se encienden y se miden desde Novedades.
 */
export type AnuncioAudiencia = 'all' | 'agents' | 'office_admins';

export interface Anuncio {
  _id:        string;
  /** Se genera del título al crear y NO cambia: vive en `user.avisosVistos` para siempre. */
  clave:      string;
  /** Cómo se muestra: tarjeta en la esquina (`notificacion`) o lámina a pantalla completa (default). */
  tipo?:      'lamina' | 'notificacion';
  eyebrow:    string;
  titulo:     string;
  bajada?:    string;
  puntos:     string[];
  destacado?: { etiqueta?: string; valor: string; copiable?: boolean } | null;
  figura?:    'gustavo' | null;
  /** Imagen propia arriba del título. Es una URL de S3: el archivo no viaja en el anuncio. */
  figuraUrl?: string | null;
  cta?:       { texto: string; accion?: 'gustavo' | null; href?: string | null } | null;
  cerrar?:    string;
  audiencia:  AnuncioAudiencia;
  roles:      string[];
  /** ⛔ Con mails cargados el anuncio está EN PRUEBA: solo esas casillas lo ven. */
  soloEmails: string[];
  officeId?:  string | null;
  tenantId?:  string | null;
  activo:     boolean;
  desde?:     string | null;
  hasta?:     string | null;
  orden:      number;
  creadoPorNombre?: string;
  createdAt:  string;
  updatedAt:  string;
}

export interface AnuncioMetricas {
  clave:              string;
  /** Denominador honesto: los activos que entran en el alcance del anuncio. */
  alcanzables:        number;
  vieron:             number;
  fueronAlDestino:    number;
  /** El número que importa: lo sacaron de encima sin entrar. */
  cerraronSinEntrar:  number;
  gente: Array<{
    userId: string; nombre: string; email: string | null; oficina: string | null;
    mostrado: string | null; cerrado: string | null; destino: string | null;
  }>;
}

export type AccionAnuncio = 'mostrado' | 'cerrado' | 'destino';

export const anunciosApi = api.injectEndpoints({
  endpoints: (build) => ({
    /** El que le toca a quien está mirando, resuelto en el servidor. */
    getAnuncioPendiente: build.query<Anuncio | null, void>({
      query: () => '/anuncios/pendiente',
      transformResponse: (r: { data: Anuncio | null }) => r.data,
      providesTags: ['AnuncioPendiente'],
    }),

    /**
     * Deja constancia de lo que la persona hizo. No invalida nada: es fuego y olvido, y si
     * falla no puede trabar el cierre del anuncio.
     */
    registrarAnuncio: build.mutation<{ ok: boolean }, { clave: string; accion: AccionAnuncio }>({
      query: (body) => ({ url: '/anuncios/registrar', method: 'POST', body }),
    }),

    getAnunciosManage: build.query<Anuncio[], void>({
      query: () => '/anuncios/manage',
      transformResponse: (r: { data: Anuncio[] }) => r.data,
      providesTags: ['Anuncio'],
    }),

    getAnuncioMetricas: build.query<AnuncioMetricas, string>({
      query: (id) => `/anuncios/${id}/metricas`,
      transformResponse: (r: { data: AnuncioMetricas }) => r.data,
      providesTags: (_r, _e, id) => [{ type: 'AnuncioMetricas' as const, id }],
    }),

    crearAnuncio: build.mutation<Anuncio, Partial<Anuncio> & { deToda?: boolean }>({
      query: (body) => ({ url: '/anuncios', method: 'POST', body }),
      transformResponse: (r: { data: Anuncio }) => r.data,
      invalidatesTags: ['Anuncio', 'AnuncioPendiente'],
    }),

    actualizarAnuncio: build.mutation<Anuncio, { id: string; body: Partial<Anuncio> }>({
      query: ({ id, body }) => ({ url: `/anuncios/${id}`, method: 'PUT', body }),
      transformResponse: (r: { data: Anuncio }) => r.data,
      invalidatesTags: ['Anuncio', 'AnuncioPendiente'],
    }),

    encenderAnuncio: build.mutation<Anuncio, { id: string; activo: boolean }>({
      query: ({ id, activo }) => ({ url: `/anuncios/${id}/activo`, method: 'PATCH', body: { activo } }),
      transformResponse: (r: { data: Anuncio }) => r.data,
      invalidatesTags: ['Anuncio', 'AnuncioPendiente'],
    }),

    /** Sube la imagen y devuelve su URL. El anuncio guarda la URL, no el archivo. */
    subirFiguraAnuncio: build.mutation<{ figuraUrl: string }, FormData>({
      query: (body) => ({ url: '/anuncios/figura', method: 'POST', body }),
      transformResponse: (r: { data: { figuraUrl: string } }) => r.data,
    }),

    borrarAnuncio: build.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/anuncios/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Anuncio', 'AnuncioPendiente'],
    }),
  }),
});

export const {
  useGetAnuncioPendienteQuery, useRegistrarAnuncioMutation,
  useGetAnunciosManageQuery, useGetAnuncioMetricasQuery,
  useCrearAnuncioMutation, useActualizarAnuncioMutation, useSubirFiguraAnuncioMutation,
  useEncenderAnuncioMutation, useBorrarAnuncioMutation,
} = anunciosApi;
